import React, { useState, useMemo, useEffect } from 'react';
import { ThemeProvider, type Direction } from '@mui/material/styles';
import { themes } from './theme';
import { useTranslation } from 'react-i18next';
import { CacheProvider } from '@emotion/react';
import { cacheLtr, cacheRtl } from '../directionCache';
import type {
  FsThemeContextProviderProps,
  Language,
  ThemeMode,
  ThemeTemplate,
} from './model';
import { FsThemeContext } from './hooks/useFsTheme';
import { Box } from '@mui/material';

const FsThemeContextProvider: React.FC<FsThemeContextProviderProps> = ({
  children,
}) => {
  const { i18n } = useTranslation();

  // 1) THEME TEMPLATE
  const [themeTemplate, setThemeTemplateState] = useState<ThemeTemplate>(() => {
    const storedThemeTemplate = localStorage.getItem('themeTemplate');
    // If we have something in localStorage, use it (type cast as needed).
    // Otherwise, default to 'default'.
    return (storedThemeTemplate as ThemeTemplate) ?? 'default';
  });

  const [mode, setModeState] = useState<ThemeMode>(() => {
    const storedMode = localStorage.getItem('mode');
    return (storedMode as ThemeMode) ?? 'dark';
  });

  const [fontSize, setFontSizeState] = useState<number>(() => {
    const storedFontSize = localStorage.getItem('fontSize');
    return storedFontSize ? parseInt(storedFontSize, 10) : 16;
  });

  const [fontWeight, setFontWeightState] = useState<number>(() => {
    const storedFontWeight = localStorage.getItem('fontWeight');
    return storedFontWeight ? parseInt(storedFontWeight, 10) : 2;
  });

  const [language, setLanguageState] = useState<Language>(() => {
    return (i18n.language as Language) || 'en';
  });

  const setThemeTemplate = (name: ThemeTemplate) => {
    setThemeTemplateState(name);
    localStorage.setItem('themeTemplate', name);
  };

  const setMode = (mode: ThemeMode) => {
    setModeState(mode);
    localStorage.setItem('mode', mode);
  };

  const setFontSize = (size: number) => {
    setFontSizeState(size);
    localStorage.setItem('fontSize', String(size));
  };

  const setFontWeight = (weight: number) => {
    setFontWeightState(weight);
    localStorage.setItem('fontWeight', String(weight));
  };

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    i18n.changeLanguage(lang);
  };

  const direction: Direction = language === 'fa' ? 'rtl' : 'ltr';

  const scaleTypography = (baseSize: number, weightLevel: number) => {
    const baseWeights: Record<number, number> = {
      1: 300,
      2: 400,
      3: 500,
      4: 700,
      5: 900,
    };
    const fontWeight = baseWeights[weightLevel] || 2;

    return {
      fontSize: baseSize,
      h1: { fontSize: baseSize * 2.5, fontWeight: fontWeight + 200 },
      h2: { fontSize: baseSize * 2.25, fontWeight: fontWeight + 150 },
      h3: { fontSize: baseSize * 2, fontWeight: fontWeight + 100 },
      h4: { fontSize: baseSize * 1.75, fontWeight: fontWeight },
      h5: { fontSize: baseSize * 1.5, fontWeight: fontWeight },
      h6: { fontSize: baseSize * 1.25, fontWeight: fontWeight },
      subtitle1: { fontSize: baseSize * 1.1, fontWeight: fontWeight },
      subtitle2: { fontSize: baseSize, fontWeight: fontWeight },
      body1: { fontSize: baseSize * 0.9, fontWeight: fontWeight },
      body2: { fontSize: baseSize * 0.8, fontWeight: fontWeight - 100 },
      caption: { fontSize: baseSize * 0.75, fontWeight: fontWeight - 100 },
      button: { fontSize: baseSize * 0.85, fontWeight: fontWeight + 100 },
      overline: { fontSize: baseSize * 0.7, fontWeight: fontWeight - 100 },
    };
  };

  const theme = useMemo(() => {
    const baseTheme = themes[themeTemplate][mode];
    return {
      ...baseTheme,
      typography: {
        ...baseTheme?.typography,
        ...scaleTypography(fontSize, fontWeight),
      },
      direction,
    };
  }, [themeTemplate, mode, direction, fontSize, fontWeight]);

  useEffect(() => {
    document.body.dir = direction;
  }, [direction]);

  const cache = useMemo(
    () => (direction === 'rtl' ? cacheRtl : cacheLtr),
    [direction]
  );

  const contextValue = useMemo(
    () => ({
      themeTemplate,
      setThemeTemplate,
      mode,
      setMode,
      language,
      setLanguage,
      fontSize,
      setFontSize,
      fontWeight,
      setFontWeight,
      direction,
    }),
    [
      themeTemplate,
      mode,
      direction,
      language,
      setLanguage,
      fontSize,
      fontWeight,
    ]
  );

  return (
    <FsThemeContext.Provider value={contextValue}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <Box className={language == 'fa' ? 'fa-num' : ''}>{children}</Box>
        </ThemeProvider>
      </CacheProvider>
    </FsThemeContext.Provider>
  );
};

export default FsThemeContextProvider;
